/**
 * The art contract (manifest.json), typed: the families of art the game draws, each one .glb that
 * `pnpm art:build` makes from art/families/<family>.blend, and the rules every kind keeps. The
 * Blender build reads the same file, so the two sides can't disagree. See docs/ART.md.
 */
import manifest from './manifest.json';

export interface ArtKind {
  /** The family (and .glb) it comes from. */
  family: string;
  /** At most this many triangles. */
  triangles: number;
  /** The top's height must fall in this range, in world units (a tile is about 1.2 across). */
  height: readonly [number, number];
  /** No part reaches further from its origin than this, in the ground plane. */
  footprint: number;
  /** How far the wind moves its top in the game. */
  wind: number;
}

/** Kind names survive Blender, glTF and three.js alike. */
const NAME = /^[a-z][a-z0-9-]*$/;

/** Every kind of every family, by name, read from the manifest and checked as it is read. */
function kinds(): ReadonlyMap<string, ArtKind> {
  const out = new Map<string, ArtKind>();
  for (const [family, spec] of Object.entries(manifest.families)) {
    for (const [name, kind] of Object.entries(spec.kinds)) {
      const [low, high] = kind.height;
      if (!NAME.test(name) || low === undefined || high === undefined)
        throw new Error(`manifest.json: ${name} breaks the contract's own rules`);
      out.set(name, { ...kind, family, height: [low, high] });
    }
  }
  return out;
}

export const ART_KINDS = kinds();

/** How far below the ground (its origin) any model may reach: roots sinking in a little. */
export const ART_SINK: number = manifest.sink;

/** The families, each with its kinds' names in the manifest's order. */
export const ART_FAMILIES: ReadonlyMap<string, readonly string[]> = new Map(
  Object.entries(manifest.families).map(([family, spec]) => [family, Object.keys(spec.kinds)]),
);
